import * as React from 'react';
import styled from 'styled-components';
import { ITeam } from '../../../../data/types';
import { useEntities } from '../../../../hooks/useEntities';
import { SelectBox } from '../SelectBox';
import { ListItem, ListItemCell } from '../../../shared/List/ListItem';

interface IProps {
  selectedTeamIds: string[];
  onChange: (teamIds: string[]) => void;
  disabled?: boolean;
}

export const MultiTeamSelect = (props: IProps): React.ReactElement => {
  const { teams, loading } = useEntities();
  const { selectedTeamIds } = props;

  const handleTeamToggle = (team: ITeam, isChecked: boolean): void => {
    if (props.disabled) {
      return; // Prevent changes when disabled
    }
    let newSelectedIds = [...selectedTeamIds];
    if (isChecked) {
      newSelectedIds.push(team.id);
    } else {
      newSelectedIds = newSelectedIds.filter(
        (id: string): boolean => id !== team.id
      );
    }
    props.onChange(newSelectedIds);
  };

  if (loading) {
    return <LoadingMessage>Loading teams...</LoadingMessage>;
  }

  if (teams.length === 0) {
    return <EmptyMessage>No teams available</EmptyMessage>;
  }

  return (
    <Container>
      <HelpText>Restrict the token to one or more teams.</HelpText>
      <TeamListContainer>
        <TeamList>
          {teams.map(
            (team: ITeam): React.ReactElement => (
              <StyledListItem key={team.id}>
                <ListItemCell>
                  <TeamName>{team.name}</TeamName>
                </ListItemCell>
                <ListItemCell>
                  <SelectBox
                    selected={selectedTeamIds.includes(team.id)}
                    onSelect={(e: React.ChangeEvent<HTMLInputElement>): void =>
                      handleTeamToggle(team, e.target.checked)
                    }
                    disabled={props.disabled}
                    ariaLabel={`Select ${team.name}`}
                  />
                </ListItemCell>
              </StyledListItem>
            )
          )}
        </TeamList>
      </TeamListContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HelpText = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-bottom: 8px;
`;

const TeamListContainer = styled.div`
  border: 1px solid var(--color-separator);
  border-radius: 4px;
  background: var(--color-list-background);
`;

const TeamList = styled.div`
  width: 100%;
`;

const StyledListItem = styled(ListItem)`
  padding: 0;
  border-bottom: 1px solid var(--color-separator);
  background: transparent;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--color-list-item-hover);
  }
`;

const TeamName = styled.span`
  color: var(--color-text);
  font-size: 14px;
`;

const LoadingMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: var(--color-text-secondary);
`;

const EmptyMessage = styled.div`
  padding: 16px;
  text-align: center;
  color: var(--color-text-secondary);
`;
